import { useState } from "react";
import { api, API, setSessionToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, Eye, EyeOff, LockKeyhole, ShieldCheck } from "lucide-react";
import { BBC_LOGO_URL } from "@/lib/brand";

export default function AdminLogin() {
  const { user, loading, setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const signInEmail = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setSessionToken(data.session_token);
      setUser(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "We could not sign you in. Check your details and try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleLogin = () => {
    const state = Array.from(window.crypto.getRandomValues(new Uint8Array(32)), n => n.toString(16).padStart(2, "0")).join("");
    sessionStorage.setItem("bbc.oauth_state", state);
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `${API}/auth/google/start?redirect=${encodeURIComponent(redirectUrl)}&client_state=${state}`;
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] text-[#5C6661]" role="status">Loading secure sign in…</div>;
  }
  if (user && user.is_admin) return <Navigate to="/admin" replace />;
  const isPending = user && (user.verification_status === "pending" || user.role === "pending");

  return (
    <div className="relative min-h-screen bg-[#FAF9F6] flex items-center justify-center px-5 py-10 sm:px-6" data-testid="admin-login-page">
      <a href="#admin-login-form" className="skip-link">Skip to sign in</a>
      <Link to="/" className="absolute left-5 top-5 inline-flex min-h-11 items-center gap-2 rounded-full px-3 text-sm font-medium text-[#2C4C3B] hover:bg-[#F3EFEA] sm:left-8 sm:top-8">
        <ArrowLeft aria-hidden="true" className="h-4 w-4" /> Back to website
      </Link>

      <main className="max-w-md w-full pt-14">
        <div className="text-center">
          <img src={BBC_LOGO_URL} alt="BBC Barani seal" className="w-16 h-16 mx-auto object-contain mb-4" />
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8A5A2A]">Content management</p>
          <h1 className="mt-2 font-serif-display text-4xl sm:text-5xl text-[#1C201E]">Welcome back</h1>
          <p className="mt-3 text-[#5C6661] text-sm leading-6">Sign in to manage Bible Baptist Church Barani’s website and community content.</p>
        </div>

        <div id="admin-login-form" className="mt-8 p-6 sm:p-8 bg-white border border-[#E5E0D8] rounded-3xl shadow-[0_20px_60px_rgba(26,51,38,0.08)]">
          {error && <p role="alert" className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p>}

          <form onSubmit={signInEmail} className="space-y-5 mb-6">
            <div>
              <label htmlFor="admin-email" className="block text-sm font-medium text-[#1C201E]">Email address</label>
              <input id="admin-email" type="email" inputMode="email" autoCapitalize="none" autoComplete="username" required value={email} onChange={e => setEmail(e.target.value)} className="mt-2 block min-h-12 w-full rounded-xl border border-[#CFC8BE] bg-white px-4 text-base" />
            </div>
            <div>
              <label htmlFor="admin-password" className="block text-sm font-medium text-[#1C201E]">Password</label>
              <div className="relative mt-2">
                <input id="admin-password" type={showPassword ? "text" : "password"} autoComplete="current-password" required value={password} onChange={e => setPassword(e.target.value)} className="block min-h-12 w-full rounded-xl border border-[#CFC8BE] bg-white px-4 pr-12 text-base" />
                <button type="button" onClick={() => setShowPassword(value => !value)} className="absolute inset-y-0 right-0 flex w-12 items-center justify-center rounded-r-xl text-[#5C6661] hover:text-[#2C4C3B]" aria-label={showPassword ? "Hide password" : "Show password"}>
                  {showPassword ? <EyeOff aria-hidden="true" className="h-5 w-5" /> : <Eye aria-hidden="true" className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <button disabled={busy} className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full bg-[#2C4C3B] px-5 font-semibold text-white hover:bg-[#1A3326] disabled:cursor-wait disabled:opacity-70">
              <LockKeyhole aria-hidden="true" className="h-4 w-4" />{busy ? "Signing in…" : "Sign in securely"}
            </button>
          </form>

          <div className="relative my-6"><div className="border-t border-[#E5E0D8]"/><span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-3 text-xs uppercase tracking-wider text-[#5C6661]">or</span></div>
          <button data-testid="google-login-button" onClick={handleLogin} disabled={busy} className="w-full inline-flex min-h-12 items-center justify-center gap-3 px-6 py-3 rounded-full border border-[#2C4C3B] bg-white font-semibold text-[#2C4C3B] hover:bg-[#F3EFEA] transition-colors disabled:opacity-60">
            <ShieldCheck aria-hidden="true" className="w-5 h-5" />
            Continue with Google
          </button>

          {user && !user.is_admin && !isPending && user.role !== "visitor" && (
            <div data-testid="not-admin-warning" className="mt-6 p-4 bg-red-50 border border-red-100 rounded-xl text-sm text-red-700">
              You are signed in as <b>{user.email}</b> but this account is not an authorized admin.
            </div>
          )}
          {isPending && (
            <div data-testid="pending-warning" className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
              You are signed in as <b>{user.email}</b>. Your BBC membership verification is pending. You can read the Bible, Daily Verse, and Daily Devotion while it is reviewed.
              {!user.profile_complete && <a href="/profile" className="mt-3 block font-semibold underline">Complete Profile</a>}
            </div>
          )}
          {user?.role === "visitor" && <div className="mt-6 p-4 bg-sky-50 border border-sky-100 rounded-xl text-sm text-sky-900">This is a Visitor account. Bible, Daily Verse, and Daily Devotion are available in BBC Connect.</div>}

          <p className="mt-6 flex items-center justify-center gap-2 text-xs text-[#5C6661] text-center">
            <ShieldCheck aria-hidden="true" className="h-4 w-4" />
            Only authorized admin accounts can edit church content.
          </p>
        </div>
      </main>
    </div>
  );
}
